import { useState } from "react";
import { Button, Card, EmptyState, MetricTile, RuntimeDiagnosticBanner, type ComponentDiagnostic } from "@swot/base-components";
import type { PermissionContext, WorkflowAction, WorkflowCommand } from "@swot/swot-components";
import { CmtsEnterpriseModal } from "./CmtsEnterpriseModal.js";
import { CmtsEnterpriseForm } from "./forms/index.js";
import { throwCmtsCanonicalError } from "@cmts/api/cmtsApiErrors.js";
import { normalizeUserSafeApiError } from "@cmts/api/apiErrorNormalizer.js";

function actionCode(action: WorkflowAction): string {
  const record = action as unknown as Record<string, unknown>;
  return String(record.actionCode ?? record.code ?? record.id ?? record.label ?? "ACTION").trim();
}
function actionLabel(action: WorkflowAction): string {
  const record = action as unknown as Record<string, unknown>;
  return String(record.label ?? record.name ?? record.actionCode ?? record.code ?? "Workflow action");
}
function actionNextStatus(action: WorkflowAction): string {
  const record = action as unknown as Record<string, unknown>;
  return String(record.toStatus ?? record.nextStatus ?? record.targetStatus ?? "backend-driven");
}
function actionRequiresReason(code: string): boolean {
  return ["REJECT", "REOPEN", "ESCALATE", "CLOSE", "APPROVE_CLOSURE", "REJECT_CLOSURE"].some((token) => code.toUpperCase().includes(token));
}
function commandId(code: string): string {
  return `cmd-${code.toLowerCase()}-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

export function CmtsWorkflowCommandPanel(props: {
  complaintId: string;
  status: string;
  actorId: string;
  actorName?: string | undefined;
  actions: readonly WorkflowAction[];
  permissionContext?: PermissionContext | null | undefined;
  diagnostics?: readonly ComponentDiagnostic[] | undefined;
  onCommand: (command: WorkflowCommand) => void | Promise<void>;
}) {
  const [selected, setSelected] = useState<WorkflowAction | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const code = selected ? actionCode(selected) : "";
  const needsReason = actionRequiresReason(code);
  const fields = [
    { name: "comment", label: "Remarks / comment", type: "textarea" as const, required: true, minLength: 5, help: "Explain the business reason for this workflow action." },
    ...(needsReason ? [{ name: "reason", label: "Decision reason", type: "textarea" as const, required: true, minLength: 5, help: "Required for rejection, escalation, closure or reopen decisions." }] : []),
    { name: "assigneeId", label: "Assignee / owner", placeholder: "Optional user id for assignment or next action owner" },
    { name: "dueDate", label: "Due date", type: "datetime-local" as const },
  ];

  return (
    <Card title="Workflow command center" subtitle="Execute backend-guarded workflow actions with RBAC, SoD, evidence and closure blocker validation.">
      <RuntimeDiagnosticBanner diagnostics={props.diagnostics ?? []} title="Workflow diagnostics" />
      <div className="cmts-workflow-command-summary">
        <MetricTile label="Current status" value={props.status || "backend-driven"} tone="info" />
        <MetricTile label="Available actions" value={props.actions.length} tone="info" />
        <MetricTile label="Actor" value={props.actorName || props.actorId || "Current user"} tone="info" />
      </div>
      {props.actions.length === 0 ? <EmptyState title="No workflow actions available" message="No workflow actions are currently available for this persona, status and SoD context." /> : null}
      <div className="cmts-workflow-action-grid">
        {props.actions.map((action) => {
          const currentCode = actionCode(action);
          return (
            <Card key={currentCode} title={actionLabel(action)} subtitle={`Next: ${actionNextStatus(action)}`}>
              <Button label={currentCode} variant="secondary" onClick={() => { setError(null); setSelected(action); }} />
            </Card>
          );
        })}
      </div>
      <CmtsEnterpriseModal open={Boolean(selected)} title={selected ? actionLabel(selected) : "Workflow action"} subtitle={`Current status: ${props.status || "backend-driven"} • Next: ${selected ? actionNextStatus(selected) : "-"}`} size="lg" onClose={() => !busy ? setSelected(null) : undefined}>
        {selected ? (
          <CmtsEnterpriseForm
            fields={fields}
            submitLabel="Execute workflow action"
            busy={busy}
            error={error}
            onCancel={() => setSelected(null)}
            onSubmit={async (payload) => {
              try {
                setBusy(true);
                setError(null);
                if (!code || code === "ACTION") throwCmtsCanonicalError({ code: "CMTS_ERR_MISSING_ACTION_CODE", message: "Workflow action code is missing from the selected action.", details: { selected } });
                if (!props.actorId || !props.actorId.trim()) throwCmtsCanonicalError({ code: "CMTS_ERR_MISSING_ACTOR", message: "Workflow command requires an actor id.", details: { complaintId: props.complaintId, actionCode: code } });
                if (!props.complaintId || !props.complaintId.trim()) throwCmtsCanonicalError({ code: "CMTS_ERR_MISSING_ENTITY", message: "Workflow command requires a persisted complaint id.", details: { actionCode: code } });
                await props.onCommand({
                  commandId: commandId(code),
                  recordId: props.complaintId,
                  recordType: "CMTS_COMPLAINT",
                  actionCode: code,
                  actorId: props.actorId,
                  actorName: props.actorName,
                  currentStatus: props.status,
                  fromStatus: props.status,
                  requiredSideEffects: [],
                  issuedAt: new Date().toISOString(),
                  payload,
                } as unknown as WorkflowCommand);
                setSelected(null);
              } catch (err) {
                setError(normalizeUserSafeApiError(err).userMessage || "Workflow action failed. The record was not changed.");
              } finally {
                setBusy(false);
              }
            }}
          >
            <div className="cmts-form-side-note">This command is validated by RBAC, SoD, status, required evidence and closure blockers before mutation. The live backend command persists workflow history, audit and operation-spine side effects.</div>
          </CmtsEnterpriseForm>
        ) : null}
      </CmtsEnterpriseModal>
    </Card>
  );
}
